"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useAuth } from "@/hooks/use-auth";

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type LoginValues = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const router = useRouter();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: LoginValues) => {
    setError(null);
    try {
      await login(values.email, values.password);
      router.push("/dashboard");
    } catch {
      setError("Invalid email or password");
    }
  };

  return (
    <div className="min-h-screen bg-[#381C51]">
      <div className="flex min-h-screen w-full overflow-hidden bg-[#2d1642]">
        <aside className="relative hidden w-1/2 border-r border-cyan-300/25 bg-[radial-gradient(circle_at_20%_10%,rgba(0,173,238,0.35),transparent_45%),linear-gradient(180deg,#381C51_0%,#241235_100%)] p-10 text-slate-100 lg:flex lg:flex-col lg:justify-between">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:36px_36px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(0,173,238,0.25),transparent_38%),radial-gradient(circle_at_80%_78%,rgba(255,255,255,0.08),transparent_42%)]"
          />

          <div className="relative z-10 inline-flex items-center gap-2 text-2xl font-bold tracking-wide text-[#00ADEE]">
            Vsquared Asset Platform
          </div>
          <div className="relative z-10 space-y-4">
            <h2 className="max-w-xl text-5xl font-semibold leading-tight text-white">
              Track assets with control and clarity.
            </h2>
            <p className="max-w-xl text-xl leading-relaxed text-slate-300">
              Manage devices, assignments, and maintenance records from one secure workspace.
            </p>
          </div>
        </aside>

        <section className="relative flex flex-1 items-center justify-center bg-white px-6 py-10 sm:px-10">

          <span className="absolute right-6 top-6 z-10 text-xs font-semibold uppercase tracking-[0.18em] text-[#381C51]">
            Login
          </span>

          <div className="relative z-10 w-full max-w-md rounded-2xl border border-[#381C51]/15 bg-white p-6 text-slate-900 shadow-[0_20px_60px_rgba(56,28,81,0.14)] sm:p-8">
            <div className="mb-7 space-y-2">
              <h1 className="text-2xl font-semibold text-[#381C51]">Sign in to your workspace</h1>
              <p className="text-sm text-slate-600">Use your email and password to continue.</p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-700">Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="name@company.com"
                          autoComplete="email"
                          className="h-11 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#00ADEE]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-700">Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          className="h-11 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#00ADEE]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {error ? <p className="text-sm text-red-500">{error}</p> : null}
                <Button type="submit" className="mt-2 h-11 w-full bg-[#00ADEE] text-white hover:bg-[#0097cf]">
                  Sign in
                </Button>
              </form>
            </Form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
